/**
 * NotificationBell — Header bell icon with unread badge and dropdown.
 *
 * Only shows UNREAD notifications in the dropdown. Clicking a notification
 * marks it as read and navigates to the relevant page.
 */

import { useState, useRef, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../hooks/useNotifications';
import {
  markNotificationRead,
  isExternalNotificationLink,
} from '../../services/notificationService';

export function NotificationBell() {
  const { notifications, unreadCount, refresh } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  const handleNotificationClick = async (notification: { id: string; linkTo: string; read: boolean }) => {
    setIsOpen(false);
    if (!notification.read) {
      markNotificationRead(notification.id).then(() => refresh()).catch(() => {});
    }
    if (isExternalNotificationLink(notification.linkTo)) {
      window.open(notification.linkTo, '_blank', 'noopener,noreferrer');
    } else {
      navigate(notification.linkTo);
    }
  };

  // Only show unread notifications in the dropdown
  const unreadNotifications = notifications.filter((n) => !n.read).slice(0, 5);

  return (
    <div ref={containerRef} className="relative">
      {/* Bell button */}
      <button
        onClick={handleToggle}
        className="relative inline-flex items-center justify-center rounded-md p-2 opacity-60 hover:opacity-100 transition-opacity"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        title="Notifications"
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-80 rounded-md border bg-popover text-popover-foreground shadow-lg z-50">
          <div className="flex items-center justify-between px-3 py-2 border-b">
            <p className="text-sm font-medium text-foreground">Notifications</p>
          </div>

          <div className="max-h-64 overflow-y-auto">
            {unreadNotifications.length === 0 ? (
              <div className="px-3 py-6 text-center">
                <p className="text-xs text-muted-foreground">
                  No new notifications
                </p>
              </div>
            ) : (
              unreadNotifications.map((notification) => (
                <button
                  key={notification.id}
                  type="button"
                  className="w-full text-left flex flex-col px-3 py-2.5 hover:bg-accent transition-colors border-b last:border-b-0"
                  onClick={() => handleNotificationClick(notification)}
                >
                  <span className="text-sm font-medium text-foreground">
                    {notification.title}
                  </span>
                  <span className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                    {notification.body}
                  </span>
                </button>
              ))
            )}
          </div>

          {/* View all link */}
          <div className="border-t px-3 py-2">
            <button
              type="button"
              className="text-xs text-primary hover:text-primary/80 transition-colors"
              onClick={() => { setIsOpen(false); navigate('/notifications'); }}
            >
              View all notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
